<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/campaign_registrations/csv"
      :filter="exportFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>

  <div class="ml-12">
    <div
      v-if="
        campaignRegistrationRecordsRegistered && campaignRegistrationRecordsRegistered?.length > 0
      "
      class="mb-24"
    >
      <h2 class="font-bold">{{ $t('pages.driver.campaignList.registeredCampaigns') }}</h2>

      <div class="flex flex-wrap gap-4 mt-7.5">
        <campaign-pane
          v-for="campaignRegistration in campaignRegistrationRecordsRegistered"
          :campaign-registration="campaignRegistration"
        />
      </div>
    </div>

    <div
      v-if="campaignRegistrationRecordsOngoing && campaignRegistrationRecordsOngoing?.length > 0"
      class="mb-24"
    >
      <h2 class="font-bold">{{ $t('pages.driver.campaignList.activeCampaigns') }}</h2>

      <div class="flex flex-wrap gap-4 mt-7.5">
        <campaign-pane
          v-for="campaignRegistration in campaignRegistrationRecordsOngoing"
          :campaign-registration="campaignRegistration"
        />
      </div>
    </div>

    <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
      {{ $t('pages.driver.campaignList.tableHeader') }}
    </div>

    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, useProvider, type ProviderParams } from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Driver, HydraCollection, CampaignRegistration, Campaign } from '@/entities/Entities';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import CampaignPane from '@utils/CampaignPane.vue';
import { useVModel } from '@vueuse/core';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'campaign.actualStartDate');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.campaignsFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});
const exportFilter = computed(() => {
  return { driver: props.modelValue?.id ?? '', ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const filterFuture = computed(() => ({
  driver: props.modelValue?.id ?? '',
  timeStatus: 'future',
  pagination: 'false',
}));
const providerParamsFuture = reactive({
  filter: filterFuture,
});

const filterOngoing = computed(() => ({
  driver: props.modelValue?.id ?? '',
  timeStatus: 'ongoing',
  pagination: 'false',
}));
const providerParamsOngoing = reactive({
  filter: filterOngoing,
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'campaign.group.name',
    title: t('campaignGroup.name'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    to: (registration: CampaignRegistration) =>
      registration?.campaign?.group?.id
        ? {
            name: 'campaign-group-details',
            params: { campaignGroupId: registration?.campaign?.group?.id },
          }
        : undefined,
    filter: {
      key: 'campaign.group',
      type: 'dropdown',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('campaignGroup.name'),
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        createOption: true,
        resolveOnLoad: true,
        options: (query: string, select: any) => {
          const input = query || select.modelValue;
          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Campaign>('/campaign_groups', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
      },
    },
  },
  {
    key: 'timeStatus',
    title: t('campaign.timeStatus.DEFAULT'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: false,
    visible: true,
    computed: (registration: CampaignRegistration) => {
      const { campaign } = registration;
      if (!campaign) return '';
      const { actualStartDate, actualEndDate } = campaign;
      if (!actualStartDate || !actualEndDate) return '';
      const now = new Date();
      if (now < new Date(actualStartDate)) return t('campaign.timeStatus.future');
      if (now > new Date(actualEndDate)) return t('campaign.timeStatus.past');
      return t('campaign.timeStatus.ongoing');
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.timeStatus.DEFAULT'),
        options: [
          {
            label: t('campaign.timeStatus.ongoing'),
            value: 'ongoing',
          },
          {
            label: t('campaign.timeStatus.future'),
            value: 'future',
          },
          {
            label: t('campaign.timeStatus.past'),
            value: 'past',
          },
        ]
      },
    },
  },
  {
    key: 'campaign.actualStartDate',
    title: t('campaign.startDate'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.startDate'),
      },
    },
  },
  {
    key: 'campaign.actualEndDate',
    title: t('campaign.endDate'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('campaign.endDate'),
      },
    },
  },
  {
    key: 'drivenDistance',
    title: t('campaignRegistration.drivenDistance'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        icon: <span>km</span>,
        label: t('campaignRegistration.drivenDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'drivenTime',
    title: t('campaignRegistration.drivenTime'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: number) => formatTimeDurationFromSeconds(v),
    filter: {
      type: 'duration',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('campaignRegistration.drivenTime'),
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('campaignRegistration.numOfTours'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('campaignRegistration.numOfTours'),
      },
    },
  },
  {
    key: 'numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.driver.campaignList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driver.campaignList.filterGroups.general'),
      options: {
        label: t('driver.numOfDaysWithTour'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const blockRequest = computed(() => !props.modelValue);

const { data: items, loading: loadingOld } = useProvider<HydraCollection<CampaignRegistration>>(
  '/campaign_registrations',
  providerParams,
  { disabled: blockRequest }
);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const { data: itemsFuture, loading: loadingFuture } = useProvider<
  HydraCollection<CampaignRegistration>
>('/campaign_registrations', providerParamsFuture, {
  disabled: blockRequest,
});
const campaignRegistrationRecordsRegistered = computed(() => unref(itemsFuture)?.['hydra:member']);

const { data: itemsOngoing, loading: loadingOngoing } = useProvider<
  HydraCollection<CampaignRegistration>
>('/campaign_registrations', providerParamsOngoing, {
  disabled: blockRequest,
});
const campaignRegistrationRecordsOngoing = computed(() => unref(itemsOngoing)?.['hydra:member']);

const loading = computed(() => loadingOld.value || loadingFuture.value || loadingOngoing.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
